import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Pencil, X } from "lucide-react";
import { useState } from "react";
import type { Variant, WriteRejection } from "@estelle/domain";

import { useDataSource } from "@/adapters/data-source";
import { can, usePermissions } from "@/auth/permissions";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RejectionNotice } from "@/modules/catalog/screens/RejectionNotice";

/**
 * What the form is editing: the code being chosen, and the `channelUpdatedAt`
 * this screen observed when editing started.
 *
 * `guard` is captured once, at the moment the pencil is clicked, and never
 * re-read from `variant` afterwards. `variant` is a query result and can be
 * silently replaced by a background refetch while the form is open (a tab
 * refocus, another mutation's broad invalidation); reading the guard fresh
 * at submit time would pick up that newer value and defeat the conflict
 * check entirely — the whole point is to send what this screen actually
 * showed someone, not whatever the cache holds a moment later.
 */
interface EditState {
  draft: string;
  guard: string;
}

/**
 * Inline HSN edit for one variant (FR-CAT-6, M6-4).
 *
 * The write path, not the input, is the point of this ticket: `setVariantHsn`
 * takes a `Guarded` and returns a `WriteResult` rather than throwing, and
 * both of those shape everything below. A rejected write never touches
 * `editing` or `variant` — the form stays open with the draft still in it and
 * the rejection shown, and the row itself is unchanged because nothing here
 * ever writes the draft into local state.
 */
export function HsnEditor({ variant }: { variant: Variant }) {
  const data = useDataSource();
  const queryClient = useQueryClient();
  const permissions = usePermissions();
  const canEdit = can(permissions, "catalog.variant.editHsn");

  const [editing, setEditing] = useState<EditState | null>(null);
  const [rejection, setRejection] = useState<WriteRejection | null>(null);

  // Loaded only once editing starts, not on every render of a variant table —
  // a product with three variants should not fire three HSN-master queries
  // before anyone has clicked the pencil on any of them.
  const codesQuery = useQuery({
    queryKey: ["catalog", "hsn", "list-for-edit"],
    queryFn: () => data.catalog.listHsnCodes({ limit: 100 }),
    enabled: editing !== null,
  });

  const mutation = useMutation({
    mutationFn: (input: { hsnCode: string; guard: string }) =>
      data.catalog.setVariantHsn(variant.id, input.hsnCode, {
        expectedUpdatedAt: input.guard,
      }),
    onSuccess: (result) => {
      if (result.ok) {
        setEditing(null);
        setRejection(null);
        // Write-through, never write-local: the row is redrawn from what
        // this invalidation fetches back from the channel, never from
        // `input.hsnCode`. Invalidating the whole "catalog" key rather than
        // one query so the product list's HSN-coverage badge and the
        // Missing HSN worklist both drop this variant the moment it is
        // fixed, instead of showing it as still missing until their own
        // screens happen to remount.
        void queryClient.invalidateQueries({ queryKey: ["catalog"] });
      } else {
        setRejection(result.rejection);
      }
    },
  });

  if (editing === null) {
    return (
      <div className="flex items-center gap-2">
        {variant.hsnCode ? (
          <span className="tabular-nums">{variant.hsnCode}</span>
        ) : (
          <Badge variant="outline" className="font-normal">
            No HSN code
          </Badge>
        )}
        {canEdit ? (
          <Button
            type="button"
            size="icon-sm"
            variant="ghost"
            aria-label="Edit HSN code"
            onClick={() => {
              setRejection(null);
              setEditing({
                draft: variant.hsnCode ?? "",
                guard: variant.channelUpdatedAt,
              });
            }}
          >
            <Pencil />
          </Button>
        ) : null}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-2">
        <Select
          value={editing.draft}
          onValueChange={(value) => {
            setEditing({ ...editing, draft: value });
          }}
        >
          <SelectTrigger size="sm" className="w-56">
            <SelectValue placeholder="Choose an HSN code" />
          </SelectTrigger>
          <SelectContent>
            {(codesQuery.data?.items ?? []).map((code) => (
              <SelectItem key={code.id} value={code.code}>
                {code.code} — {code.description} ({code.gstRate}%)
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          type="button"
          size="icon-sm"
          variant="ghost"
          disabled={editing.draft === "" || mutation.isPending}
          onClick={() => {
            mutation.mutate({ hsnCode: editing.draft, guard: editing.guard });
          }}
        >
          <Check />
        </Button>
        <Button
          type="button"
          size="icon-sm"
          variant="ghost"
          disabled={mutation.isPending}
          onClick={() => {
            setEditing(null);
            setRejection(null);
          }}
        >
          <X />
        </Button>
      </div>

      {rejection ? (
        <RejectionNotice
          rejection={rejection}
          onReload={() => {
            void queryClient.invalidateQueries({ queryKey: ["catalog"] });
            setEditing(null);
            setRejection(null);
          }}
        />
      ) : null}
    </div>
  );
}
